import { Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { handleImageUpload } from "./admin-utils";

export function PromotionModal({ open, onOpenChange, editingPromo, promoForm, setPromoForm, onSave, saving, locations = [] }) {
  const toggleLocation = (slug) => {
    setPromoForm((f) => {
      const current = f.available_locations || [];
      const next = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug];
      return { ...f, available_locations: next };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{editingPromo ? "Edit Promotion" : "Add Promotion"}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[65vh] pr-4">
          <div className="space-y-4">
            <div>
              <Label>Background Image</Label>
              <div
                className="mt-2 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-[#1aa59a] transition-colors h-24 flex items-center justify-center"
                onClick={() => document.getElementById("promo-image-input").click()}
              >
                {promoForm.image_base64 ? (
                  <img src={promoForm.image_base64} alt="Preview" className="max-h-full max-w-full object-cover rounded" />
                ) : (
                  <div className="text-gray-400">
                    <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs">Upload banner</p>
                  </div>
                )}
              </div>
              <input
                id="promo-image-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, (img) => setPromoForm({ ...promoForm, image_base64: img }))}
              />
            </div>

            <div>
              <Label htmlFor="promo-title">Title</Label>
              <Input
                id="promo-title"
                value={promoForm.title}
                onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="promo-subtitle">Subtitle</Label>
              <Input
                id="promo-subtitle"
                value={promoForm.subtitle}
                onChange={(e) => setPromoForm({ ...promoForm, subtitle: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="promo-link">Link URL (optional)</Label>
              <Input
                id="promo-link"
                type="url"
                value={promoForm.link_url}
                onChange={(e) => setPromoForm({ ...promoForm, link_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <Label className="font-medium">Where to show this promotion</Label>
              <p className="text-xs text-gray-500">Select one or more places this promotion appears.</p>

              <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white transition-colors">
                <input
                  type="checkbox"
                  checked={promoForm.is_public}
                  onChange={(e) => setPromoForm({ ...promoForm, is_public: e.target.checked })}
                  className="w-4 h-4 accent-[#1aa59a]"
                  data-testid="promo-target-public"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Main site (public)</div>
                  <div className="text-xs text-gray-500">officemeals.munchdmeals.com</div>
                </div>
              </label>

              {locations.length === 0 ? (
                <p className="text-xs text-gray-400 italic pl-2">
                  No locations created yet. Add locations in the Locations tab to enable targeting.
                </p>
              ) : (
                locations.map((loc) => (
                  <label
                    key={loc.slug}
                    className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={(promoForm.available_locations || []).includes(loc.slug)}
                      onChange={() => toggleLocation(loc.slug)}
                      className="w-4 h-4 accent-[#1aa59a]"
                      data-testid={`promo-target-${loc.slug}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium truncate">{loc.name}</div>
                      <div className="text-xs text-gray-500">/loc/{loc.slug}</div>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="promo-active">Active</Label>
              <Switch
                id="promo-active"
                checked={promoForm.is_active}
                onCheckedChange={(checked) => setPromoForm({ ...promoForm, is_active: checked })}
              />
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#1aa59a] hover:bg-[#158a80]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
